// Canvas compositor: draws a main source full-frame with an optional
// picture-in-picture bubble (bottom-right, rounded), and exposes the result
// as a constant 1280x720@30 video track. Used for screenshare+camera PiP —
// swapping which source is "main" is instant (same output track, no pipeline
// change).

const W = 1280;
const H = 720;
const FPS = 30;
const PIP_RATIO = 0.26;
const PIP_MARGIN = 24;
const PIP_RADIUS = 14;

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// cover-fit draw (crop source to fill the destination rect)
function drawCover(ctx, videoEl, dx, dy, dw, dh) {
  const vw = videoEl.videoWidth;
  const vh = videoEl.videoHeight;
  if (!vw || !vh) return;
  const scale = Math.max(dw / vw, dh / vh);
  const sw = dw / scale;
  const sh = dh / scale;
  ctx.drawImage(videoEl, (vw - sw) / 2, (vh - sh) / 2, sw, sh, dx, dy, dw, dh);
}

export function createCompositor({ mainTrack, pipTrack }) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  const elements = new Map(); // track -> playing off-DOM <video>
  const elFor = (track) => {
    let el = elements.get(track);
    if (!el) {
      el = document.createElement("video");
      el.muted = true;
      el.playsInline = true;
      el.srcObject = new MediaStream([track]);
      el.play().catch(() => {});
      elements.set(track, el);
    }
    return el;
  };

  let main = mainTrack;
  let pip = pipTrack || null;

  const draw = () => {
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, W, H);
    if (main && main.readyState === "live") drawCover(ctx, elFor(main), 0, 0, W, H);
    if (pip && pip.readyState === "live") {
      const pw = Math.round(W * PIP_RATIO);
      const ph = Math.round((pw * 9) / 16);
      const px = W - pw - PIP_MARGIN;
      const py = H - ph - PIP_MARGIN;
      ctx.save();
      roundRectPath(ctx, px, py, pw, ph, PIP_RADIUS);
      ctx.clip();
      drawCover(ctx, elFor(pip), px, py, pw, ph);
      ctx.restore();
      roundRectPath(ctx, px, py, pw, ph, PIP_RADIUS);
      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(255,255,255,0.85)";
      ctx.stroke();
    }
  };
  const timer = setInterval(draw, 1000 / FPS);

  const outStream = canvas.captureStream(FPS);
  const outTrack = outStream.getVideoTracks()[0];

  return {
    track: outTrack,
    swap() {
      if (pip) [main, pip] = [pip, main];
    },
    setPip(track) {
      pip = track;
    },
    setMain(track) {
      main = track;
    },
    // Replace a source wherever it currently sits (survives prior swaps).
    replace(oldTrack, newTrack) {
      if (main === oldTrack) main = newTrack;
      if (pip === oldTrack) pip = newTrack;
      elements.delete(oldTrack);
    },
    stop() {
      clearInterval(timer);
      outStream.getTracks().forEach((t) => t.stop());
      for (const el of elements.values()) el.srcObject = null;
      elements.clear();
    },
  };
}
